import RequestMethod from "@/models/client/RequestMethod";
import RestResponse from "@/models/client/RestResponse";
import {
  DashboardResponse,
  WidgetTemplateResponse,
  CreateDashboardRequest,
  UpdateDashboardRequest,
  PublishDashboardRequest,
  ShareDashboardRequest,
  CloneDashboardAsNewRequest,
  CreateWidgetTemplateRequest,
  UpdateWidgetTemplateRequest,
} from "@/models/dashboard/Dashboard";
import { RestClient } from "./restClient";

export type DashboardServiceType = {
  // Dashboards
  getDashboards: () => RestResponse<DashboardResponse[]>;
  getDashboard: (id: string) => RestResponse<DashboardResponse>;
  createDashboard: (
    request: CreateDashboardRequest
  ) => RestResponse<DashboardResponse>;
  updateDashboard: (
    id: string,
    request: UpdateDashboardRequest
  ) => RestResponse<DashboardResponse>;
  deleteDashboard: (id: string) => RestResponse<void>;
  setDefaultDashboard: (id: string) => RestResponse<void>;
  publishDashboard: (
    id: string,
    request: PublishDashboardRequest
  ) => RestResponse<DashboardResponse>;
  shareDashboardWithSubTenants: (
    id: string,
    request: ShareDashboardRequest
  ) => RestResponse<DashboardResponse>;
  cloneDashboard: (id: string) => RestResponse<DashboardResponse>;
  cloneDashboardAsNew: (
    id: string,
    request: CloneDashboardAsNewRequest
  ) => RestResponse<DashboardResponse>;

  // Widget Templates
  getTemplates: () => RestResponse<WidgetTemplateResponse[]>;
  getTemplate: (id: string) => RestResponse<WidgetTemplateResponse>;
  createTemplate: (
    request: CreateWidgetTemplateRequest
  ) => RestResponse<WidgetTemplateResponse>;
  updateTemplate: (
    id: string,
    request: UpdateWidgetTemplateRequest
  ) => RestResponse<WidgetTemplateResponse>;
  deleteTemplate: (id: string) => RestResponse<void>;
};

const dashboardService = (
  client: RestClient,
  path: string
): DashboardServiceType => ({
  // Dashboards
  getDashboards: async () =>
    await client.fetch<DashboardResponse[]>(RequestMethod.GET, path),

  getDashboard: async (id: string) =>
    await client.fetch<DashboardResponse>(RequestMethod.GET, `${path}/${id}`),

  createDashboard: async (request: CreateDashboardRequest) =>
    await client.fetch<DashboardResponse>(RequestMethod.POST, path, request),

  updateDashboard: async (id: string, request: UpdateDashboardRequest) =>
    await client.fetch<DashboardResponse>(
      RequestMethod.PUT,
      `${path}/${id}`,
      request
    ),

  deleteDashboard: async (id: string) =>
    await client.fetch<void>(RequestMethod.DELETE, `${path}/${id}`),

  setDefaultDashboard: async (id: string) =>
    await client.fetch<void>(RequestMethod.PUT, `${path}/${id}/set-default`),

  publishDashboard: async (id: string, request: PublishDashboardRequest) =>
    await client.fetch<DashboardResponse>(
      RequestMethod.PUT,
      `${path}/${id}/publish`,
      request
    ),

  shareDashboardWithSubTenants: async (
    id: string,
    request: ShareDashboardRequest
  ) =>
    await client.fetch<DashboardResponse>(
      RequestMethod.PUT,
      `${path}/${id}/share`,
      request
    ),

  cloneDashboard: async (id: string) =>
    await client.fetch<DashboardResponse>(
      RequestMethod.POST,
      `${path}/${id}/clone`
    ),

  cloneDashboardAsNew: async (id: string, request: CloneDashboardAsNewRequest) =>
    await client.fetch<DashboardResponse>(
      RequestMethod.POST,
      `${path}/${id}/clone-as-new`,
      request
    ),

  // Widget Templates
  getTemplates: async () =>
    await client.fetch<WidgetTemplateResponse[]>(
      RequestMethod.GET,
      `${path}/templates`
    ),

  getTemplate: async (id: string) =>
    await client.fetch<WidgetTemplateResponse>(
      RequestMethod.GET,
      `${path}/templates/${id}`
    ),

  createTemplate: async (request: CreateWidgetTemplateRequest) =>
    await client.fetch<WidgetTemplateResponse>(
      RequestMethod.POST,
      `${path}/templates`,
      request
    ),

  updateTemplate: async (id: string, request: UpdateWidgetTemplateRequest) =>
    await client.fetch<WidgetTemplateResponse>(
      RequestMethod.PUT,
      `${path}/templates/${id}`,
      request
    ),

  deleteTemplate: async (id: string) =>
    await client.fetch<void>(RequestMethod.DELETE, `${path}/templates/${id}`),
});

export default dashboardService;
